/**
 * Seat — a single seat ring at the table. Owner: Phase-3 A3.1.
 *
 * Visual: a circular avatar ring (initials or icon) with the display name
 * underneath, a hand-count chip beside it, a glowing turn indicator when it
 * is this seat's turn, a partner-highlight ring when this seat is on the
 * local player's team, a "bot" badge when occupant is a bot, and a small
 * "connected" dot.
 *
 * Data sources (props are layout-only; everything else is read from the
 * store via hooks, matching the binding contract A3.2/A3.3 depend on):
 *   - `useLobby()` for the seat occupant (displayName / kind / connected).
 *   - `useView()` for handCounts, callerSeat, and bidding/play state.
 *   - `useRecentEvents()` to surface "bot took over" + "player reclaimed"
 *     transitions if needed (kept minimal here; A3.3 may extend).
 *
 * The "is it this seat's turn?" logic is computed up in Table.tsx (which
 * knows the round phase) and passed implicitly: this component listens to
 * an `is-active` className that Table.tsx flips by setting the prop
 * `data-active` via the wrapping slot. We compute activeness internally
 * too so the seat is self-contained — but Table.tsx remains the source
 * of truth for redundancy.
 *
 * Props (FIXED contract — DO NOT change without A3.2/A3.3 sign-off):
 *   seatIndex   engine seat index 0..n-1
 *   layoutSlot  rotated index (0 = bottom = local player)
 *   isYou       true iff seatIndex == useYou()
 *   isPartner   true iff this seat is on the local player's team (and not you)
 *   isHost      true iff this seat's playerId == lobby.hostId
 *
 * RULES §1 (seats/teams), §3.1 (caller), §5.1 (leader), PRD FR-U1.
 */
import { useEffect, useRef, useState } from "react";
import { useLobby, useRecentEvents, useView } from "../state/hooks.js";
import { seatSlotStyle } from "./Felt.js";

/** v1.4 Phase 20 (trick-end animation revamp): how long the "this seat just
 * won the trick" pulse stays applied to the seat ring. Matches the Trick
 * collection animation timing (settle 200ms + winner pulse 350ms +
 * collection flight 550ms ≈ 1.1 s) so the gold ring stays on through the
 * cards arriving at the winner's seat. */
const TRICK_WINNER_PULSE_MS = 1200;

export interface SeatProps {
  seatIndex: number;
  layoutSlot: number;
  isYou: boolean;
  isPartner: boolean;
  isHost: boolean;
}

function initialsOf(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).slice(0, 2);
  return parts.map((p) => p.charAt(0).toUpperCase()).join("") || "?";
}

/**
 * Compute "active" — whose turn is it right now — given the redacted view.
 * Mirrors the Table.tsx computation so this component is self-consistent
 * when rendered in isolation.
 */
function isActiveSeat(seat: number, view: ReturnType<typeof useView>): boolean {
  if (!view) return false;
  if (view.gameOver) return false;
  if (view.phase === "bidding") return view.bidding.toAct === seat;
  if (view.phase === "playing") {
    const t = view.trick;
    if (!t) return false;
    if (t.plays.length === 0) return t.leaderSeat === seat;
    const last = t.plays[t.plays.length - 1];
    if (!last) return t.leaderSeat === seat;
    const n = view.config.players;
    return ((last.seat + 1) % n) === seat;
  }
  return false;
}

export function Seat({
  seatIndex,
  layoutSlot,
  isYou,
  isPartner,
  isHost,
}: SeatProps): JSX.Element {
  const lobby = useLobby();
  const view = useView();
  const recentEvents = useRecentEvents();
  const n = view?.config.players ?? lobby?.config.players ?? 4;
  const seatInfo = lobby?.seats.find((s) => s.seatIndex === seatIndex);
  const name = seatInfo?.displayName ?? `Seat ${seatIndex}`;
  const isBot = seatInfo?.kind === "bot";
  const connected = seatInfo?.connected ?? false;
  // v1.4.1 Phase 22.2: three-state seat-conn LED — green when connected,
  // amber while reconnecting (within server GRACE_MS), red once timed out.
  // `reconnecting` is absent from v1.4.0-and-earlier lobby payloads (and from
  // bot / never-joined seats), so undefined falls through to the red branch
  // for a disconnected seat.
  const connState: "connected" | "reconnecting" | "offline" = connected
    ? "connected"
    : seatInfo?.reconnecting
      ? "reconnecting"
      : "offline";
  const handCount = view?.handCounts[seatIndex] ?? 0;
  const isCaller = view?.callerSeat === seatIndex;
  const isHighBidder = view?.bidding.highBidder === seatIndex;
  // RULES §4.2 v1.1: explicit pass is gone — there's no per-seat "passed"
  // state to surface. The auction-end timer drives termination instead, and
  // any non-action is an implicit pass. Field removed from PublicBidding.
  const active = isActiveSeat(seatIndex, view);
  const team = seatIndex % 2 === 0 ? "A" : "B";
  // Slot 0 (bottom) is "you" in the canonical layout. We still accept the
  // explicit isYou prop because the slot rotation is computed up in
  // Table.tsx and we don't want to duplicate that arithmetic here.
  const style = seatSlotStyle(layoutSlot, n as 4 | 6);
  // Surface only the most recent bot/reclaim event for this seat (decorative).
  const lastSeatEvent = [...recentEvents]
    .reverse()
    .find(
      (e) =>
        (e.event.type === "botTookOver" || e.event.type === "playerReclaimed") &&
        e.event.seat === seatIndex,
    );
  const showBotBadge = isBot || lastSeatEvent?.event.type === "botTookOver";

  // v1.4 Phase 20 (trick-end animation revamp): detect the most recent
  // trickWon event and pulse this seat's ring for ~1.2 s if it's the
  // winning seat.
  //
  // Post-stress-test fix: the original effect re-ran on every
  // `recentEvents` change (cardPlayed, bidPlaced, etc.) and reset the
  // 1.2-s setTimeout. Result: as long as ANY event landed within 1.2 s
  // the old winner's pulse refreshed and never expired — visually the
  // stale winner's seat kept glowing well into the next trick. Fix:
  // gate the effect on a ref-tracked `latestHandledSeq` so the timer
  // only fires ONCE per new trickWon event. Other recentEvents updates
  // are a no-op for this seat.
  const [isRecentTrickWinner, setIsRecentTrickWinner] =
    useState<boolean>(false);
  const lastTrickWonSeqRef = useRef<number>(-1);
  useEffect(() => {
    // Find the latest trickWon event in the recent-events tail.
    let latestSeq = -1;
    let latestWinnerSeat: number | null = null;
    for (const entry of recentEvents) {
      if (entry.event.type === "trickWon" && entry.seq > latestSeq) {
        latestSeq = entry.seq;
        latestWinnerSeat = entry.event.seat;
      }
    }
    // Only act on NEW trickWon events. A re-run of this effect because
    // some other event (cardPlayed, bidPlaced, etc.) updated
    // recentEvents must be a no-op here — otherwise we'd reset the
    // pulse setTimeout and the old winner's ring would never stop.
    if (latestSeq === -1 || latestSeq <= lastTrickWonSeqRef.current) {
      return;
    }
    lastTrickWonSeqRef.current = latestSeq;
    if (latestWinnerSeat !== seatIndex) return;
    setIsRecentTrickWinner(true);
    const id = window.setTimeout(() => {
      setIsRecentTrickWinner(false);
    }, TRICK_WINNER_PULSE_MS);
    return () => window.clearTimeout(id);
  }, [recentEvents, seatIndex]);

  return (
    <div
      className={[
        "seat",
        isYou ? "seat-you" : "",
        isPartner ? "seat-partner" : "",
        active ? "seat-active" : "",
        isRecentTrickWinner ? "seat-trick-winner" : "",
        `seat-team-${team}`,
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
      data-seat={seatIndex}
      data-slot={layoutSlot}
      aria-label={`Seat ${seatIndex}: ${name}${isBot ? " (bot)" : ""}${active ? " — to act" : ""}`}
    >
      <div className="seat-ring">
        <div className="seat-avatar" aria-hidden="true">
          <span className="seat-initials">{initialsOf(name)}</span>
        </div>
        {active ? <div className="seat-turn-ring" aria-hidden="true" /> : null}
        {handCount > 0 ? (
          <div className="seat-hand-chip" aria-label={`${handCount} cards`}>
            <span className="seat-hand-chip-icon" aria-hidden="true">🂠</span>
            <span className="seat-hand-chip-count">{handCount}</span>
          </div>
        ) : null}
        {showBotBadge ? <div className="seat-bot-badge">BOT</div> : null}
        {isCaller ? <div className="seat-caller-badge" title="Caller">C</div> : null}
        {isHighBidder ? (
          <div className="seat-bidder-badge" title="High bidder">B</div>
        ) : null}
        <span
          className={`seat-conn-dot seat-conn-dot-${connState}`}
          aria-hidden="true"
        />
      </div>
      <div className="seat-meta">
        <span className="seat-name">
          {isHost ? <span className="seat-host-marker" title="Host">★</span> : null}
          {name}
        </span>
      </div>
    </div>
  );
}
